/**
 * MJOS-189 / DEM-42 — Engineering Dashboard v2.
 *
 * Full process-insights layout (1:1 with Elad's ProcessInsightsPage) backed by
 * REAL client-scoped Zeus data. Live snapshot tiles + range-toggled historical
 * charts + statistical analysis + insights derived from the live telemetry.
 *
 * Data paths:
 *   • /api/zeus/last-known-values/:stationId — live snapshot, 15 s polling
 *   • /api/zeus/historical/:stationId?range=…&channels=… — range-bound history
 *     for the flow / pressure / stats / histogram / scatter cards (5 min TTL)
 *   • /api/zeus/historical/:stationId?range=30d&channels=<flow>
 *     — fixed 30-day pull for Daily Volume (last 7 days), Volume This Month,
 *     System Uptime
 *
 * Where Zeus does not expose a metric cleanly (PT-203 currently reads 0;
 * deltas vs prior period need 2× the data window) we show N/A or omit the
 * delta rather than fabricating numbers.
 */

import { useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  BarChart2,
  CheckCircle2,
  Droplet,
  Gauge,
  Lightbulb,
  Sigma,
  Sparkles,
  TrendingUp,
  Waves,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { useApi } from "@/lib/api";

// ─── Pilot config — Ozone Nano Bubbles / Shomrat Gadol ──────────────────
const PILOT_STATION_ID = "0U5B3XY4";
const PROCESS_NAME = "Shomrat Gadol — Ozone Nano Bubbles";
const PROCESS_SUBTITLE =
  "HERMES_M103 · 3-stage ozone treatment · Water / SCADA";
const FLOW_UNIT = "m³/h";
const VOLUME_UNIT = "m³";
const PRESSURE_UNIT = "bar";
const PRESSURE_MAX = 6.0;
const LIVE_REFETCH_MS = 15_000;
const HIST_REFETCH_MS = 5 * 60_000;

// Channel mapping for this station (verified live, MJOS-187):
//   0  = FM-102 flow         (m³/h)   — primary flow signal
//   29 = Dissolved O₂ temp   (°C)
//   30 = DO-403              (mg/l)
//   53 = PT-101 pressure     (Bar)    — primary pressure signal
//                                       (PT-203 / ch 3 currently reads 0)
const CH_FLOW = 0;
const CH_PRESSURE = 53;
const CH_DO = 30;
const CH_TEMP = 29;
const RANGE_CHANNELS = [CH_FLOW, CH_PRESSURE, CH_DO, CH_TEMP];
const VOLUME_RANGE = "30d" as const;
const EXPECTED_SAMPLES_PER_DAY = 288; // 5-minute Zeus polling

const RANGES = ["24h", "7d", "30d"] as const;
type Range = (typeof RANGES)[number];

// ─── Types ──────────────────────────────────────────────────────────────
interface Sample {
  t: string;
  v: Record<string, number>;
}
interface HistoricalResp {
  stationId: string;
  stationLabel: string | null;
  range: Range;
  startDate: string;
  endDate: string;
  channels: number[];
  samples: Sample[];
  count: number;
  servedFromCache: boolean;
  cacheTtlSeconds: number;
  fetchedAt: string;
}
interface LiveResp {
  stationId: string;
  stationLabel: string | null;
  lastConnectionTime: string | null;
  rssi: number | null;
  powerSupply: string | null;
  values: Record<string, number>;
  servedFromCache: boolean;
  cacheTtlSeconds: number;
  fetchedAt: string;
}

// ─── Number/format helpers ──────────────────────────────────────────────
function fmt(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits === 0 ? 0 : 1,
  });
}

function describeStats(values: number[]) {
  const v = values.filter((x) => Number.isFinite(x));
  if (v.length === 0) return null;
  const sorted = [...v].sort((a, b) => a - b);
  const sum = v.reduce((a, b) => a + b, 0);
  const avg = sum / v.length;
  const median =
    v.length % 2 === 0
      ? (sorted[v.length / 2 - 1] + sorted[v.length / 2]) / 2
      : sorted[(v.length - 1) / 2];
  const variance = v.reduce((acc, x) => acc + (x - avg) ** 2, 0) / v.length;
  return {
    min: sorted[0],
    max: sorted[sorted.length - 1],
    avg,
    median,
    stddev: Math.sqrt(variance),
    count: v.length,
  };
}

// Average samples for a single channel into fixed-size time buckets so SVG
// area charts stay readable across 24h / 7d / 30d.
function bucketAvg(
  samples: Sample[],
  ch: number,
  bucketMs: number,
): { ts: number; v: number }[] {
  const buckets = new Map<number, { sum: number; count: number }>();
  for (const s of samples) {
    const val = s.v[String(ch)];
    if (val == null || !Number.isFinite(val)) continue;
    const ts = Date.parse(s.t);
    if (!Number.isFinite(ts)) continue;
    const key = Math.floor(ts / bucketMs) * bucketMs;
    const b = buckets.get(key) ?? { sum: 0, count: 0 };
    b.sum += val;
    b.count += 1;
    buckets.set(key, b);
  }
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a - b)
    .map(([ts, { sum, count }]) => ({ ts, v: sum / count }));
}

function rangeBucketSize(range: Range): number {
  if (range === "24h") return 60 * 60 * 1000; // 24 hourly buckets
  if (range === "7d") return 4 * 60 * 60 * 1000; // 42 four-hour buckets
  return 12 * 60 * 60 * 1000; // 60 twelve-hour buckets (30d)
}

function bucketLabel(range: Range, ts: number): string {
  const d = new Date(ts);
  if (range === "24h") return String(d.getUTCHours()).padStart(2, "0");
  return `${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
}

function dailyVolumeFromFlow(
  samples: Sample[],
  days: number,
): { label: string; v: number; iso: string }[] {
  const byDay = new Map<string, number>();
  for (const s of samples) {
    const val = s.v[String(CH_FLOW)];
    if (val == null || !Number.isFinite(val)) continue;
    const d = new Date(s.t);
    if (Number.isNaN(d.getTime())) continue;
    const dayKey = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
    byDay.set(dayKey, (byDay.get(dayKey) ?? 0) + val * (5 / 60));
  }
  const labels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const out: { label: string; v: number; iso: string }[] = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(
      Date.UTC(
        today.getUTCFullYear(),
        today.getUTCMonth(),
        today.getUTCDate() - i,
      ),
    );
    const dayKey = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
    out.push({
      label: labels[d.getUTCDay()],
      v: byDay.get(dayKey) ?? 0,
      iso: dayKey,
    });
  }
  return out;
}

// ─── SVG primitives — ported from Elad's ProcessInsightsPage (1:1) ──────
function SvgArea({
  data,
  color,
  showEvery = 1,
  thresholds = [],
  emptyLabel = "No data",
}: {
  data: { label: string; v: number }[];
  color: string;
  showEvery?: number;
  thresholds?: { value: number; color: string }[];
  emptyLabel?: string;
}) {
  const CL = 38,
    CR = 450,
    CT = 12,
    CB = 158,
    CW = CR - CL,
    CH = CB - CT;
  if (data.length === 0) {
    return (
      <svg viewBox="0 0 480 180" className="w-full h-full">
        <text
          x={(CL + CR) / 2}
          y={(CT + CB) / 2}
          textAnchor="middle"
          fontSize="12"
          fill="#94A3B8"
        >
          {emptyLabel}
        </text>
        <line
          x1={CL}
          y1={CB}
          x2={CR}
          y2={CB}
          stroke="#D0DEF0"
          strokeWidth="1.5"
        />
      </svg>
    );
  }
  const vals = data.map((d) => d.v);
  const dMinRaw = Math.min(...vals);
  const dMaxRaw = Math.max(...vals, ...thresholds.map((t) => t.value));
  const dMin = dMinRaw === dMaxRaw ? dMinRaw - 1 : dMinRaw * 0.92;
  const dMax = dMinRaw === dMaxRaw ? dMaxRaw + 1 : dMaxRaw * 1.06;
  const n = data.length;
  const xAt = (i: number) => CL + (n <= 1 ? 0 : (i / (n - 1)) * CW);
  const yAt = (v: number) => CB - ((v - dMin) / (dMax - dMin)) * CH;
  const pts = data
    .map((d, i) => `${xAt(i).toFixed(1)},${yAt(d.v).toFixed(1)}`)
    .join(" ");
  const area = `M ${xAt(0).toFixed(1)},${CB} L ${pts} L ${xAt(n - 1).toFixed(1)},${CB} Z`;
  const gid = `ag${color.replace(/[^a-zA-Z0-9]/g, "")}`;
  const yLevels = [0, 0.25, 0.5, 0.75, 1].map((p) => dMin + (dMax - dMin) * p);
  return (
    <svg viewBox="0 0 480 180" className="w-full h-full">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0.01" />
        </linearGradient>
      </defs>
      {yLevels.map((v, i) => {
        const y = yAt(v);
        const lbl =
          v >= 1000
            ? `${(v / 1000).toFixed(0)}k`
            : v < 10
              ? v.toFixed(1)
              : Math.round(v).toString();
        return (
          <g key={i}>
            <line
              x1={CL}
              y1={y}
              x2={CR}
              y2={y}
              stroke="#E5EEF9"
              strokeWidth="1"
            />
            <text
              x={CL - 5}
              y={y + 4}
              textAnchor="end"
              fontSize="9"
              fill="#94A3B8"
            >
              {lbl}
            </text>
          </g>
        );
      })}
      {thresholds.map((t, i) => {
        const y = yAt(t.value);
        if (y < CT || y > CB) return null;
        return (
          <line
            key={i}
            x1={CL}
            y1={y}
            x2={CR}
            y2={y}
            stroke={t.color}
            strokeWidth="1.5"
            strokeDasharray="5,3"
          />
        );
      })}
      <path d={area} fill={`url(#${gid})`} />
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {data.map((d, i) => {
        if (i % showEvery !== 0) return null;
        return (
          <text
            key={i}
            x={xAt(i).toFixed(1)}
            y={CB + 13}
            textAnchor="middle"
            fontSize="9"
            fill="#94A3B8"
          >
            {d.label}
          </text>
        );
      })}
      <line
        x1={CL}
        y1={CB}
        x2={CR}
        y2={CB}
        stroke="#D0DEF0"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function SvgBars({
  data,
  color,
}: {
  data: { label: string; v: number }[];
  color: string;
}) {
  const CL = 32,
    CR = 216,
    CT = 12,
    CB = 158,
    CW = CR - CL,
    CH = CB - CT;
  const MAX = Math.max(1, ...data.map((d) => d.v)) * 1.1;
  const n = Math.max(data.length, 1);
  const section = CW / n;
  const BW = Math.min(section * 0.6, 22);
  return (
    <svg viewBox="0 0 240 180" className="w-full h-full">
      {[0, 0.5, 1].map((p, i) => {
        const v = MAX * p;
        const y = CB - p * CH;
        const lbl =
          v >= 1000 ? `${(v / 1000).toFixed(0)}k` : Math.round(v).toString();
        return (
          <g key={i}>
            <line
              x1={CL}
              y1={y}
              x2={CR}
              y2={y}
              stroke="#E5EEF9"
              strokeWidth="1"
            />
            <text
              x={CL - 4}
              y={y + 4}
              textAnchor="end"
              fontSize="9"
              fill="#94A3B8"
            >
              {lbl}
            </text>
          </g>
        );
      })}
      {data.map((d, i) => {
        const sx = CL + section * i;
        const bx = sx + (section - BW) / 2;
        const bh = (d.v / MAX) * CH;
        return (
          <g key={`${d.label}-${i}`}>
            <rect
              x={bx.toFixed(1)}
              y={(CB - bh).toFixed(1)}
              width={BW}
              height={bh.toFixed(1)}
              rx="3"
              fill={color}
              opacity="0.85"
            />
            <text
              x={(sx + section / 2).toFixed(1)}
              y={CB + 13}
              textAnchor="middle"
              fontSize="9"
              fill="#64748B"
            >
              {d.label}
            </text>
          </g>
        );
      })}
      <line
        x1={CL}
        y1={CB}
        x2={CR}
        y2={CB}
        stroke="#D0DEF0"
        strokeWidth="1.5"
      />
    </svg>
  );
}

function SvgGauge({
  value,
  max,
  unit,
}: {
  value: number | null;
  max: number;
  unit: string;
}) {
  const cx = 100,
    cy = 100,
    ro = 74;
  const v = value ?? 0;
  const p = Math.min(Math.max(v / max, 0), 1);
  const arcColor =
    v > max * 0.916 ? "#EF4444" : v > max * 0.75 ? "#F59E0B" : "#22C55E";
  const angle = Math.PI * (1 - p);
  const ex = (cx + ro * Math.cos(angle)).toFixed(1);
  const ey = (cy - ro * Math.sin(angle)).toFixed(1);
  const lge = p >= 0.5 ? 1 : 0;
  const bgPath = `M ${cx - ro} ${cy} A ${ro} ${ro} 0 0 1 ${cx + ro} ${cy}`;
  const valPath =
    p < 0.01
      ? null
      : `M ${cx - ro} ${cy} A ${ro} ${ro} 0 ${lge} 1 ${ex} ${ey}`;
  const zone =
    v > max * 0.916 ? "CRITICAL" : v > max * 0.75 ? "ELEVATED" : "NORMAL";
  return (
    <svg viewBox="0 0 200 128" className="w-full h-full">
      <path
        d={bgPath}
        fill="none"
        stroke="#E5EEF9"
        strokeWidth="14"
        strokeLinecap="round"
      />
      {valPath && (
        <path
          d={valPath}
          fill="none"
          stroke={arcColor}
          strokeWidth="14"
          strokeLinecap="round"
        />
      )}
      {valPath && (
        <circle
          cx={ex}
          cy={ey}
          r="6"
          fill="white"
          stroke={arcColor}
          strokeWidth="2.5"
        />
      )}
      <text
        x={cx}
        y={cy - 8}
        textAnchor="middle"
        fontSize="26"
        fontWeight="700"
        fill="#1E293B"
      >
        {value == null ? "—" : value.toFixed(2)}
      </text>
      <text
        x={cx}
        y={cy + 10}
        textAnchor="middle"
        fontSize="11"
        fill="#64748B"
      >
        {unit}
      </text>
      <text
        x={cx - ro}
        y={cy + 20}
        textAnchor="middle"
        fontSize="9"
        fill="#94A3B8"
      >
        0
      </text>
      <text
        x={cx + ro}
        y={cy + 20}
        textAnchor="middle"
        fontSize="9"
        fill="#94A3B8"
      >
        {max}
      </text>
      <text
        x={cx}
        y={cy + 26}
        textAnchor="middle"
        fontSize="10"
        fontWeight="600"
        fill={arcColor}
      >
        {value == null ? "" : zone}
      </text>
    </svg>
  );
}

function SvgHistogram({
  bins,
  meanValue,
}: {
  bins: { label: string; count: number }[];
  meanValue: number | null;
}) {
  const CL = 40,
    CR = 460,
    CT = 12,
    CB = 158,
    CW = CR - CL,
    CH = CB - CT;
  if (bins.length === 0) {
    return (
      <svg viewBox="0 0 480 185" className="w-full h-full">
        <text
          x={(CL + CR) / 2}
          y={(CT + CB) / 2}
          textAnchor="middle"
          fontSize="12"
          fill="#94A3B8"
        >
          No flow samples in range
        </text>
        <line
          x1={CL}
          y1={CB}
          x2={CR}
          y2={CB}
          stroke="#D0DEF0"
          strokeWidth="1.5"
        />
      </svg>
    );
  }
  const max = Math.max(...bins.map((b) => b.count), 1);
  const MAX = max + 1;
  const n = bins.length;
  const section = CW / n;
  const meanBinIdx = (() => {
    if (meanValue == null) return -1;
    // Map mean to the bin index by parsing labels "low–high" (en dash).
    for (let i = 0; i < bins.length; i++) {
      const parts = bins[i].label.split("–");
      const lo = Number(parts[0]);
      const hi = Number(parts[1]);
      if (Number.isFinite(lo) && Number.isFinite(hi)) {
        if (meanValue >= lo && meanValue < hi) return i;
        if (i === bins.length - 1 && meanValue >= hi) return i;
      }
    }
    return -1;
  })();
  const meanBinX =
    meanBinIdx >= 0 ? CL + section * meanBinIdx + section / 2 : null;
  return (
    <svg viewBox="0 0 480 185" className="w-full h-full">
      {[0, 0.25, 0.5, 0.75, 1].map((p, i) => {
        const v = MAX * p;
        const y = CB - p * CH;
        return (
          <g key={i}>
            <line
              x1={CL}
              y1={y}
              x2={CR}
              y2={y}
              stroke="#E5EEF9"
              strokeWidth="1"
            />
            <text
              x={CL - 5}
              y={y + 4}
              textAnchor="end"
              fontSize="9"
              fill="#94A3B8"
            >
              {Math.round(v)}
            </text>
          </g>
        );
      })}
      {bins.map((b, i) => {
        const bx = CL + section * i + 2;
        const bw = section - 4;
        const bh = (b.count / MAX) * CH;
        const isMode = b.count === max && max > 0;
        return (
          <g key={`${b.label}-${i}`}>
            <rect
              x={bx.toFixed(1)}
              y={(CB - bh).toFixed(1)}
              width={bw.toFixed(1)}
              height={bh.toFixed(1)}
              rx="3"
              fill={isMode ? "#3B82C8" : "#93C5FD"}
              opacity="0.9"
            />
            {b.count > 0 && (
              <text
                x={(bx + bw / 2).toFixed(1)}
                y={(CB - bh - 4).toFixed(1)}
                textAnchor="middle"
                fontSize="10"
                fontWeight="600"
                fill="#3B82C8"
              >
                {b.count}
              </text>
            )}
            <text
              x={(bx + bw / 2).toFixed(1)}
              y={CB + 14}
              textAnchor="middle"
              fontSize="8"
              fill="#64748B"
            >
              {b.label}
            </text>
          </g>
        );
      })}
      {meanBinX != null && meanValue != null && (
        <>
          <line
            x1={meanBinX}
            y1={CT}
            x2={meanBinX}
            y2={CB}
            stroke="#F59E0B"
            strokeWidth="1.5"
            strokeDasharray="4,3"
          />
          <text
            x={meanBinX + 4}
            y={CT + 10}
            fontSize="9"
            fill="#D97706"
            fontWeight="600"
          >
            avg {meanValue.toFixed(1)}
          </text>
        </>
      )}
      <line
        x1={CL}
        y1={CB}
        x2={CR}
        y2={CB}
        stroke="#D0DEF0"
        strokeWidth="1.5"
      />
      <text x={CL} y={CB + 26} fontSize="9" fill="#94A3B8">
        Flow Rate ({FLOW_UNIT}) — frequency over selected range
      </text>
    </svg>
  );
}

function SvgScatter({
  pts,
}: {
  pts: { x: number; y: number }[];
}) {
  const CL = 44,
    CR = 462,
    CT = 16,
    CB = 162,
    CW = CR - CL,
    CH = CB - CT;
  if (pts.length === 0) {
    return (
      <svg viewBox="0 0 480 195" className="w-full h-full">
        <text
          x={(CL + CR) / 2}
          y={(CT + CB) / 2}
          textAnchor="middle"
          fontSize="12"
          fill="#94A3B8"
        >
          No paired flow/pressure samples in range
        </text>
        <line
          x1={CL}
          y1={CB}
          x2={CR}
          y2={CB}
          stroke="#D0DEF0"
          strokeWidth="1.5"
        />
        <line
          x1={CL}
          y1={CT}
          x2={CL}
          y2={CB}
          stroke="#D0DEF0"
          strokeWidth="1.5"
        />
      </svg>
    );
  }
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const xLo = Math.min(...xs),
    xHi = Math.max(...xs);
  const yLo = Math.min(...ys),
    yHi = Math.max(...ys);
  const xMin = xLo === xHi ? xLo - 1 : xLo - (xHi - xLo) * 0.05;
  const xMax = xLo === xHi ? xHi + 1 : xHi + (xHi - xLo) * 0.05;
  const yMin = yLo === yHi ? yLo - 0.1 : yLo - (yHi - yLo) * 0.1;
  const yMax = yLo === yHi ? yHi + 0.1 : yHi + (yHi - yLo) * 0.1;
  const xAt = (v: number) => CL + ((v - xMin) / (xMax - xMin)) * CW;
  const yAt = (v: number) => CB - ((v - yMin) / (yMax - yMin)) * CH;
  // Least-squares linear fit
  const n = pts.length;
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (const p of pts) {
    num += (p.x - meanX) * (p.y - meanY);
    den += (p.x - meanX) ** 2;
  }
  const slope = den === 0 ? 0 : num / den;
  const intercept = meanY - slope * meanX;
  const trend = den === 0 ? null : { x1: xLo, x2: xHi };
  const ticksX = 5,
    ticksY = 5;
  const xStep = (xMax - xMin) / ticksX;
  const yStep = (yMax - yMin) / ticksY;
  return (
    <svg viewBox="0 0 480 195" className="w-full h-full">
      {Array.from({ length: ticksY + 1 }).map((_, i) => {
        const v = yMin + yStep * i;
        const y = yAt(v);
        return (
          <g key={`y-${i}`}>
            <line
              x1={CL}
              y1={y}
              x2={CR}
              y2={y}
              stroke="#E5EEF9"
              strokeWidth="1"
            />
            <text
              x={CL - 5}
              y={y + 4}
              textAnchor="end"
              fontSize="9"
              fill="#94A3B8"
            >
              {v.toFixed(1)}
            </text>
          </g>
        );
      })}
      {Array.from({ length: ticksX + 1 }).map((_, i) => {
        const v = xMin + xStep * i;
        const x = xAt(v);
        return (
          <g key={`x-${i}`}>
            <line
              x1={x}
              y1={CT}
              x2={x}
              y2={CB}
              stroke="#F1F5F9"
              strokeWidth="1"
            />
            <text
              x={x}
              y={CB + 13}
              textAnchor="middle"
              fontSize="9"
              fill="#94A3B8"
            >
              {v.toFixed(0)}
            </text>
          </g>
        );
      })}
      {trend && (
        <line
          x1={xAt(trend.x1).toFixed(1)}
          y1={yAt(slope * trend.x1 + intercept).toFixed(1)}
          x2={xAt(trend.x2).toFixed(1)}
          y2={yAt(slope * trend.x2 + intercept).toFixed(1)}
          stroke="#F59E0B"
          strokeWidth="1.5"
          strokeDasharray="5,3"
          opacity="0.8"
        />
      )}
      {pts.map((pt, i) => (
        <circle
          key={i}
          cx={xAt(pt.x).toFixed(1)}
          cy={yAt(pt.y).toFixed(1)}
          r="3.5"
          fill="#3B82C8"
          fillOpacity="0.55"
          stroke="#2563EB"
          strokeWidth="1"
        />
      ))}
      <line
        x1={CL}
        y1={CB}
        x2={CR}
        y2={CB}
        stroke="#D0DEF0"
        strokeWidth="1.5"
      />
      <line
        x1={CL}
        y1={CT}
        x2={CL}
        y2={CB}
        stroke="#D0DEF0"
        strokeWidth="1.5"
      />
      <text
        x={CL + CW / 2}
        y={CB + 26}
        textAnchor="middle"
        fontSize="9"
        fill="#94A3B8"
      >
        Flow Rate ({FLOW_UNIT})
      </text>
      <text
        x={12}
        y={CT + CH / 2}
        textAnchor="middle"
        fontSize="9"
        fill="#94A3B8"
        transform={`rotate(-90,12,${CT + CH / 2})`}
      >
        Pressure ({PRESSURE_UNIT})
      </text>
      <circle
        cx={CR - 90}
        cy={CT + 10}
        r="4.5"
        fill="#3B82C8"
        fillOpacity="0.55"
        stroke="#2563EB"
        strokeWidth="1"
      />
      <text x={CR - 82} y={CT + 14} fontSize="9" fill="#64748B">
        Measurement
      </text>
      {trend && (
        <>
          <line
            x1={CR - 90}
            y1={CT + 24}
            x2={CR - 78}
            y2={CT + 24}
            stroke="#F59E0B"
            strokeWidth="1.5"
            strokeDasharray="4,3"
          />
          <text x={CR - 74} y={CT + 28} fontSize="9" fill="#64748B">
            Trend
          </text>
        </>
      )}
    </svg>
  );
}

// ─── Card primitives ────────────────────────────────────────────────────
function StatCard({
  icon: Icon,
  label,
  value,
  unit,
  color,
  soft,
}: {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  unit?: string;
  color: string;
  soft: string;
}) {
  return (
    <div
      className="flex flex-col gap-3 rounded-xl border bg-card p-5 shadow-sm"
      data-testid={`stat-${label}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-muted-foreground">
          {label}
        </span>
        <div
          className="flex h-9 w-9 items-center justify-center rounded-lg"
          style={{ backgroundColor: soft }}
        >
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
      </div>
      <div className="flex items-baseline gap-2">
        <span
          className="text-2xl font-bold tracking-tight tabular-nums text-stone-900"
          data-testid={`stat-value-${label}`}
        >
          {value}
        </span>
        {unit && (
          <span className="text-sm text-muted-foreground">{unit}</span>
        )}
      </div>
    </div>
  );
}

function ChartCard({
  title,
  sub,
  icon: Icon,
  iconColor,
  iconBg,
  testId,
  children,
}: {
  title: string;
  sub?: string;
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <div
      className="rounded-xl border bg-card p-5 shadow-sm"
      data-testid={testId}
    >
      <div className="mb-4 flex items-center gap-3">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: iconBg }}
        >
          <Icon className="h-4 w-4" style={{ color: iconColor }} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-stone-900">{title}</h3>
          {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

type Severity = "ok" | "warn" | "info" | "error";
const SEV: Record<
  Severity,
  { border: string; bg: string; chip: string; chipText: string; label: string }
> = {
  ok: {
    border: "#22C55E",
    bg: "#F0FDF4",
    chip: "bg-emerald-100",
    chipText: "text-emerald-700",
    label: "OK",
  },
  warn: {
    border: "#F59E0B",
    bg: "#FFFBEB",
    chip: "bg-amber-100",
    chipText: "text-amber-700",
    label: "Attention",
  },
  error: {
    border: "#EF4444",
    bg: "#FEF2F2",
    chip: "bg-red-100",
    chipText: "text-red-700",
    label: "Critical",
  },
  info: {
    border: "#3B82C8",
    bg: "#EEF5FF",
    chip: "bg-sky-100",
    chipText: "text-sky-700",
    label: "Tip",
  },
};

function InsightCard({
  severity,
  icon: Icon,
  title,
  description,
  action,
}: {
  severity: Severity;
  icon: LucideIcon;
  title: string;
  description: ReactNode;
  action: ReactNode;
}) {
  const s = SEV[severity];
  return (
    <div
      className="flex flex-col rounded-xl border-l-4 p-4 shadow-sm"
      style={{ borderLeftColor: s.border, backgroundColor: s.bg }}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/70">
          <Icon className="h-4 w-4" style={{ color: s.border }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold leading-tight text-stone-900">
              {title}
            </h4>
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${s.chip} ${s.chipText}`}
            >
              {s.label}
            </span>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-stone-600">
            {description}
          </p>
        </div>
      </div>
      <div className="mt-3 rounded-lg bg-white/60 px-3 py-2">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">
          Recommended action
        </p>
        <p className="mt-0.5 text-xs text-stone-700">{action}</p>
      </div>
    </div>
  );
}

// ─── Hooks ──────────────────────────────────────────────────────────────
function useLive() {
  const api = useApi();
  return useQuery<LiveResp>({
    queryKey: ["zeus-live", PILOT_STATION_ID],
    queryFn: async () => {
      const res = await api(`/api/zeus/last-known-values/${PILOT_STATION_ID}`);
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
      }
      return res.json();
    },
    refetchInterval: LIVE_REFETCH_MS,
    refetchIntervalInBackground: true,
    staleTime: 0,
  });
}

function useHistorical(range: Range, channels: number[]) {
  const api = useApi();
  const chanCsv = [...channels].sort((a, b) => a - b).join(",");
  return useQuery<HistoricalResp>({
    queryKey: ["zeus-historical", PILOT_STATION_ID, range, chanCsv],
    queryFn: async () => {
      const res = await api(
        `/api/zeus/historical/${PILOT_STATION_ID}?range=${range}&channels=${chanCsv}`,
      );
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
      }
      return res.json();
    },
    refetchInterval: HIST_REFETCH_MS,
    refetchIntervalInBackground: false,
    staleTime: HIST_REFETCH_MS,
  });
}

// ─── Page ───────────────────────────────────────────────────────────────
export function EngineeringDashboardPage() {
  const [range, setRange] = useState<Range>("24h");

  const live = useLive();
  const hist = useHistorical(range, RANGE_CHANNELS);
  const vol = useHistorical(VOLUME_RANGE, [CH_FLOW]);

  // Range-bound derived series + stats
  const derived = useMemo(() => {
    const samples = hist.data?.samples ?? [];
    const bs = rangeBucketSize(range);

    const flowBuckets = bucketAvg(samples, CH_FLOW, bs);
    const flowSeries = flowBuckets.map((b) => ({
      label: bucketLabel(range, b.ts),
      v: b.v,
    }));

    const pressureBuckets = bucketAvg(samples, CH_PRESSURE, bs);
    const pressureSeries = pressureBuckets.map((b) => ({
      label: bucketLabel(range, b.ts),
      v: b.v,
    }));

    const flowVals = samples
      .map((s) => Number(s.v[String(CH_FLOW)]))
      .filter((v) => Number.isFinite(v));
    const pressureVals = samples
      .map((s) => Number(s.v[String(CH_PRESSURE)]))
      .filter((v) => Number.isFinite(v));

    const flowStats = describeStats(flowVals);
    const pressureStats = describeStats(pressureVals);

    // Build histogram of flow over the range
    let bins: { label: string; count: number }[] = [];
    if (flowStats) {
      const min = flowStats.min;
      const max = flowStats.max;
      const span = Math.max(max - min, 0.1);
      const N = 8;
      const step = span / N;
      const counts = new Array<number>(N).fill(0);
      for (const v of flowVals) {
        const idx = Math.min(N - 1, Math.max(0, Math.floor((v - min) / step)));
        counts[idx] += 1;
      }
      bins = counts.map((count, i) => {
        const lo = min + step * i;
        const hi = min + step * (i + 1);
        return {
          label: `${lo.toFixed(1)}–${hi.toFixed(1)}`,
          count,
        };
      });
    }

    // Scatter — paired (flow, pressure) at same timestamp
    const scatter: { x: number; y: number }[] = [];
    for (const s of samples) {
      const x = Number(s.v[String(CH_FLOW)]);
      const y = Number(s.v[String(CH_PRESSURE)]);
      if (Number.isFinite(x) && Number.isFinite(y) && y > 0) {
        scatter.push({ x, y });
      }
    }
    // Limit to ~250 points to keep SVG responsive
    const scatterPts = scatter.length > 250
      ? scatter.filter((_, i) => i % Math.ceil(scatter.length / 250) === 0)
      : scatter;

    // Daily volume per stat row — derived from same range data
    const dailyM3 = (() => {
      const byDay = new Map<string, number>();
      for (const s of samples) {
        const val = s.v[String(CH_FLOW)];
        if (val == null || !Number.isFinite(val)) continue;
        const d = new Date(s.t);
        if (Number.isNaN(d.getTime())) continue;
        const dayKey = `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`;
        byDay.set(dayKey, (byDay.get(dayKey) ?? 0) + val * (5 / 60));
      }
      return Array.from(byDay.values());
    })();
    const dailyStats = describeStats(dailyM3);

    return {
      flowSeries,
      pressureSeries,
      flowStats,
      pressureStats,
      dailyStats,
      bins,
      scatterPts,
      flowSamplesCount: flowVals.length,
      pressureSamplesCount: pressureVals.length,
    };
  }, [hist.data, range]);

  // 30-day derived: Daily Volume bar chart (last 7 days), Volume This Month, Uptime
  const volume = useMemo(() => {
    const samples = vol.data?.samples ?? [];
    const dailyVolume = dailyVolumeFromFlow(samples, 7);
    const monthly = dailyVolumeFromFlow(samples, 30).reduce(
      (acc, d) => acc + d.v,
      0,
    );
    const flowCount = samples.filter((s) =>
      Number.isFinite(Number(s.v[String(CH_FLOW)])),
    ).length;
    const expected = EXPECTED_SAMPLES_PER_DAY * 30;
    const uptimePct = expected > 0 ? (flowCount / expected) * 100 : null;
    return {
      dailyVolume,
      monthlyM3: monthly > 0 ? monthly : null,
      uptimePct,
      hasData: samples.length > 0,
    };
  }, [vol.data]);

  // ─── Insights ─────────────────────────────────────────────────────────
  const insights = useMemo(() => {
    const items: {
      severity: Severity;
      icon: LucideIcon;
      title: string;
      description: ReactNode;
      action: ReactNode;
    }[] = [];

    if (live.data?.lastConnectionTime) {
      items.push({
        severity: "ok",
        icon: CheckCircle2,
        title: "Station online",
        description: (
          <>
            Last sync from Zeus:{" "}
            <span className="tabular-nums">{live.data.lastConnectionTime}</span>
            .
          </>
        ),
        action: "No action needed.",
      });
    }

    // OZ-105/205/305 ozone-generator OUTPUT channels (verified live 2026-07-12).
    // Was [26,47,50] — but 47=P-301 and 50=FL-304; the real ozone outputs are 26/48/51.
    const ozChannels = [26, 48, 51] as const;
    const ozActive = ozChannels.filter(
      (i) => Number(live.data?.values?.[i]) >= 1,
    ).length;
    if (live.data) {
      items.push({
        severity: ozActive >= 2 ? "ok" : "warn",
        icon: Sparkles,
        title: `Ozone generators — ${ozActive} of 3 active`,
        description:
          ozActive >= 2
            ? "All required ozone stages are running."
            : "Fewer than 2 of 3 ozone stages active — investigate before next treatment cycle.",
        action:
          ozActive >= 2
            ? "Monitor on the next polling cycle."
            : "Check OZ-105 / OZ-205 / OZ-305 power and inlet flow.",
      });
    }

    const dO2 = Number(live.data?.values?.[CH_DO]);
    if (Number.isFinite(dO2) && dO2 > 0) {
      items.push({
        severity: dO2 >= 4 ? "ok" : "warn",
        icon: Waves,
        title: "Dissolved oxygen",
        description:
          dO2 >= 4
            ? `Reading ${dO2.toFixed(2)} mg/l — within the healthy range for treated water.`
            : `Reading ${dO2.toFixed(2)} mg/l — below typical operating band. Check aeration.`,
        action:
          dO2 >= 4
            ? "No intervention required."
            : "Inspect aeration train and DO probe calibration.",
      });
    }

    // PT-203 is the named primary pressure channel but currently inactive;
    // surface this honestly so the operator can decide.
    const pt203 = Number(live.data?.values?.[3]);
    if (Number.isFinite(pt203) && pt203 === 0) {
      items.push({
        severity: "warn",
        icon: AlertTriangle,
        title: "PT-203 reads 0 — using PT-101 as primary pressure",
        description:
          "Channel 3 (PT-203) has returned 0 across the last poll. Pressure trend and gauge fall back to PT-101 (Ch 53) which is reporting normally.",
        action:
          "Schedule a field check on PT-203. If sensor is decommissioned, update the channel mapping.",
      });
    }

    if (volume.uptimePct != null) {
      items.push({
        severity:
          volume.uptimePct >= 95
            ? "ok"
            : volume.uptimePct >= 80
              ? "warn"
              : "error",
        icon: Activity,
        title: `Telemetry uptime ${volume.uptimePct.toFixed(1)}% (30d)`,
        description: `Received ${(volume.uptimePct / 100 * EXPECTED_SAMPLES_PER_DAY * 30).toFixed(0)} of ${EXPECTED_SAMPLES_PER_DAY * 30} expected 5-minute Zeus samples for FM-102.`,
        action:
          volume.uptimePct >= 95
            ? "Healthy. No action."
            : "Review Zeus connection stability and station power.",
      });
    }

    items.push({
      severity: "info",
      icon: Lightbulb,
      title: "Range affects only this view",
      description: (
        <>
          The {range} toggle controls Flow Over Time, Pressure Trend, and the
          Statistical Analysis section. Daily Volume, Volume This Month, and
          Telemetry Uptime always use the last 30 days.
        </>
      ),
      action: "—",
    });

    return items;
  }, [live.data, range, volume.uptimePct]);

  // ─── Header status ────────────────────────────────────────────────────
  const headerSub = live.data?.lastConnectionTime
    ? `${PROCESS_SUBTITLE} · last sync ${live.data.lastConnectionTime}`
    : PROCESS_SUBTITLE;

  // Current pressure for the gauge: prefer live PT-101 (Ch 53); fall back to
  // the latest historical sample if the live row is missing or 0.
  const livePressure = (() => {
    const v = Number(live.data?.values?.[CH_PRESSURE]);
    if (Number.isFinite(v) && v > 0) return v;
    const samples = hist.data?.samples ?? [];
    for (let i = samples.length - 1; i >= 0; i--) {
      const x = Number(samples[i].v[String(CH_PRESSURE)]);
      if (Number.isFinite(x) && x > 0) return x;
    }
    return null;
  })();

  const liveFlow = (() => {
    const v = Number(live.data?.values?.[CH_FLOW]);
    return Number.isFinite(v) ? v : null;
  })();

  // ─── Render ───────────────────────────────────────────────────────────
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-10">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Engineering Dashboard
          </p>
          <h1
            className="text-2xl font-bold tracking-tight text-stone-900"
            data-testid="dashboard-title"
          >
            {PROCESS_NAME}
          </h1>
          <p
            className="mt-1 text-sm text-muted-foreground"
            data-testid="dashboard-subtitle"
          >
            {headerSub}
          </p>
        </div>
        <div
          className="flex items-center gap-1 self-start rounded-lg border bg-slate-50 p-1"
          data-testid="range-toggle"
        >
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                range === r
                  ? "bg-white text-stone-900 shadow-sm"
                  : "text-stone-500 hover:text-stone-700"
              }`}
              data-testid={`range-${r}`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Errors */}
      {(live.error || hist.error || vol.error) && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
          data-testid="dashboard-error"
        >
          <p className="font-semibold">Could not fetch some live readings</p>
          {live.error && (
            <p className="mt-1 break-all">live: {(live.error as Error).message}</p>
          )}
          {hist.error && (
            <p className="mt-1 break-all">
              historical: {(hist.error as Error).message}
            </p>
          )}
          {vol.error && (
            <p className="mt-1 break-all">
              volume: {(vol.error as Error).message}
            </p>
          )}
        </div>
      )}

      {/* KPI row */}
      <div
        className="grid grid-cols-2 gap-4 lg:grid-cols-4"
        data-testid="stat-grid"
      >
        <StatCard
          icon={Droplet}
          label="Flow Rate"
          value={liveFlow == null ? "N/A" : fmt(liveFlow, 2)}
          unit={liveFlow == null ? undefined : FLOW_UNIT}
          color="#3B82C8"
          soft="#EEF5FF"
        />
        <StatCard
          icon={Gauge}
          label="Avg Pressure"
          value={
            derived.pressureStats
              ? fmt(derived.pressureStats.avg, 2)
              : "N/A"
          }
          unit={derived.pressureStats ? PRESSURE_UNIT : undefined}
          color="#10B981"
          soft="#ECFDF5"
        />
        <StatCard
          icon={BarChart2}
          label="Volume (30d)"
          value={
            volume.monthlyM3 == null
              ? "N/A"
              : volume.monthlyM3.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })
          }
          unit={volume.monthlyM3 == null ? undefined : VOLUME_UNIT}
          color="#6366F1"
          soft="#EEF2FF"
        />
        <StatCard
          icon={Zap}
          label="Telemetry Uptime (30d)"
          value={
            volume.uptimePct == null
              ? "N/A"
              : `${volume.uptimePct.toFixed(1)}%`
          }
          color="#F59E0B"
          soft="#FFFBEB"
        />
      </div>

      {/* Flow rate + Daily volume */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ChartCard
            title="Flow Rate Over Time"
            sub={`Last ${range} · ${FLOW_UNIT}${derived.flowSeries.length === 0 ? " · loading" : ""}`}
            icon={Activity}
            iconColor="#3B82C8"
            iconBg="#EEF5FF"
            testId="card-flow-over-time"
          >
            <div className="h-52">
              <SvgArea
                data={derived.flowSeries}
                color="#3B82C8"
                showEvery={Math.max(1, Math.floor(derived.flowSeries.length / 6))}
              />
            </div>
          </ChartCard>
        </div>
        <ChartCard
          title="Daily Volume"
          sub={`Last 7 days · ${VOLUME_UNIT}/day`}
          icon={BarChart2}
          iconColor="#6366F1"
          iconBg="#EEF2FF"
          testId="card-daily-volume"
        >
          <div className="h-52">
            {volume.dailyVolume.every((d) => d.v === 0) ? (
              <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                {vol.isLoading ? "Loading…" : "No volume data in the last 7 days"}
              </div>
            ) : (
              <SvgBars data={volume.dailyVolume} color="#6366F1" />
            )}
          </div>
        </ChartCard>
      </div>

      {/* Gauge + Pressure trend */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard
          title="Current Pressure"
          sub={`Live · ${PRESSURE_UNIT} · PT-101`}
          icon={Gauge}
          iconColor="#10B981"
          iconBg="#ECFDF5"
          testId="card-pressure-gauge"
        >
          <div className="flex h-52 items-center justify-center">
            <div className="w-52">
              <SvgGauge
                value={livePressure}
                max={PRESSURE_MAX}
                unit={PRESSURE_UNIT}
              />
            </div>
          </div>
        </ChartCard>
        <div className="lg:col-span-2">
          <ChartCard
            title="Pressure Trend"
            sub={`Last ${range} · ${PRESSURE_UNIT} · PT-101`}
            icon={TrendingUp}
            iconColor="#10B981"
            iconBg="#ECFDF5"
            testId="card-pressure-trend"
          >
            <div className="h-52">
              <SvgArea
                data={derived.pressureSeries}
                color="#10B981"
                showEvery={Math.max(
                  1,
                  Math.floor(derived.pressureSeries.length / 6),
                )}
                thresholds={[
                  { value: 4.5, color: "#F59E0B" },
                  { value: 5.5, color: "#EF4444" },
                ]}
              />
            </div>
          </ChartCard>
        </div>
      </div>

      {/* Statistical Analysis */}
      <div>
        <div className="mb-4 mt-2 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
            <Sigma className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-stone-900">
              Statistical Analysis
            </h2>
            <p className="text-sm text-stone-500">
              Descriptive statistics + distribution of raw measurements (
              {range})
            </p>
          </div>
        </div>

        <div
          className="mb-4 overflow-hidden rounded-xl border bg-card shadow-sm"
          data-testid="card-stats-table"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50/60">
                {[
                  "Metric",
                  "Unit",
                  "Min",
                  "Avg",
                  "Median",
                  "Max",
                  "Std Dev",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {[
                {
                  metric: "Flow Rate",
                  unit: FLOW_UNIT,
                  s: derived.flowStats,
                  color: "#3B82C8",
                  digits: 2,
                },
                {
                  metric: "Pressure",
                  unit: PRESSURE_UNIT,
                  s: derived.pressureStats,
                  color: "#10B981",
                  digits: 2,
                },
                {
                  metric: "Daily Volume",
                  unit: `${VOLUME_UNIT}/day`,
                  s: derived.dailyStats,
                  color: "#6366F1",
                  digits: 0,
                },
              ].map((row) => (
                <tr key={row.metric} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: row.color }}
                      />
                      <span className="font-medium text-stone-800">
                        {row.metric}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-stone-500">
                    {row.unit}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-700">
                    {row.s ? fmt(row.s.min, row.digits) : "N/A"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-stone-900">
                    {row.s ? fmt(row.s.avg, row.digits) : "N/A"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-700">
                    {row.s ? fmt(row.s.median, row.digits) : "N/A"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-700">
                    {row.s ? fmt(row.s.max, row.digits) : "N/A"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-stone-500">
                    {row.s ? `±${fmt(row.s.stddev, row.digits)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <ChartCard
            title="Flow Rate Distribution"
            sub={`Frequency histogram · ${FLOW_UNIT} · ${range}`}
            icon={BarChart2}
            iconColor="#3B82C8"
            iconBg="#EEF5FF"
            testId="card-histogram"
          >
            <div className="h-56">
              <SvgHistogram
                bins={derived.bins}
                meanValue={derived.flowStats?.avg ?? null}
              />
            </div>
          </ChartCard>
          <ChartCard
            title="Pressure vs. Flow Rate"
            sub="Correlation scatter · each dot = 1 sample"
            icon={Sigma}
            iconColor="#6366F1"
            iconBg="#EEF2FF"
            testId="card-scatter"
          >
            <div className="h-56">
              <SvgScatter pts={derived.scatterPts} />
            </div>
          </ChartCard>
        </div>
      </div>

      {/* Insights */}
      <div>
        <div className="mb-4 mt-2 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
            <Lightbulb className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-stone-900">
              Insights &amp; Recommendations
            </h2>
            <p className="text-sm text-stone-500">
              Derived from the current telemetry and the last 30 days of Zeus
              samples
            </p>
          </div>
        </div>
        <div
          className="grid grid-cols-1 gap-3 md:grid-cols-2"
          data-testid="insights-grid"
        >
          {insights.map((ins, i) => (
            <InsightCard key={i} {...ins} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Preserve the named export the router uses.
export { EngineeringDashboardPage as default };
